import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api, describeError, forceRefreshTokens } from '../api';
import { Json, Message, Pill, Section } from '../components/ui';
import { useSession } from '../session';

/** change 12: GET /api/billing/products · GET /api/me/subscription · POST /api/billing/purchases/verify · POST /api/billing/restore */
export interface SubscriptionView {
  status: 'none' | 'active' | 'in_grace' | 'expired' | 'revoked';
  tier: 'free' | 'pro';
  platform: 'apple' | 'google' | 'paypal' | null;
  product_id: string | null;
  period: 'month' | 'year' | null;
  current_period_end: string | null;
  auto_renew: boolean | null;
  environment: 'production' | 'sandbox' | null;
  management_url: string | null;
}
interface Catalog {
  subscriptions: Array<{ id: string; period: 'month' | 'year'; usd: number; store_product_ids: { ios?: string; android?: string; paypal?: string } }>;
  packs: Array<{ id: string; usd: number; credits: number; bonus_pct: number; tag?: string; store_product_ids?: { ios?: string; android?: string; paypal?: string } }>;
  mock_iap: boolean;
  paypal_client_id: string | null;
  current: SubscriptionView;
}
interface PurchaseResult { granted: boolean; already_processed: boolean; transaction_id: string; subscription?: SubscriptionView; credits_added?: number; balance?: number; token_refresh_required: boolean }
interface RestoreResult { subscription: SubscriptionView; credits_synced: number; token_refresh_required: boolean }

const STATUS_LABEL: Record<SubscriptionView['status'], string> = { none: '未订阅', active: '生效中', in_grace: '宽限期（扣款重试）', expired: '已到期', revoked: '已撤销 / 退款' };
const STATUS_TONE: Record<SubscriptionView['status'], 'zinc' | 'emerald' | 'amber' | 'rose'> = { none: 'zinc', active: 'emerald', in_grace: 'amber', expired: 'zinc', revoked: 'rose' };
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');
const daysFromNow = (d: number) => new Date(Date.now() + d * 86_400_000).toISOString();
const mockId = (prefix: string) => `${prefix}-${Date.now().toString(36)}`;

/** Purchase → the tier claim in the access token is stale; refresh right away so the next call is not a 401 round-trip. */
export async function applyTierRefresh(required: boolean, reloadMe: () => Promise<void>): Promise<void> {
  if (required) await forceRefreshTokens();
  await reloadMe();
}

export function SubscriptionSection({ onCreditsChanged }: { onCreditsChanged?: () => Promise<void> | void }) {
  const { me, reloadMe, isAdmin } = useSession();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [platform, setPlatform] = useState<'apple' | 'google'>('apple');
  const [last, setLast] = useState<unknown>(null);
  const [originalTx, setOriginalTx] = useState<string>('');

  const report = (err: unknown) => setError(err instanceof ApiFailure ? describeError(err) : String(err));

  const load = useCallback(async () => {
    if (!me) return;
    setError(null);
    try {
      setCatalog((await api<Catalog>('/api/billing/products')).data);
    } catch (err) {
      report(err);
    }
  }, [me]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (label: string, fn: () => Promise<{ refresh: boolean; text: string; payload: unknown; credits?: boolean }>) => {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const out = await fn();
      setLast(out.payload);
      setMessage(`${label} · ${out.text}${out.refresh ? ' · 档位已变，已刷新令牌' : ''}`);
      await applyTierRefresh(out.refresh, reloadMe);
      await load();
      if (out.credits && onCreditsChanged) await onCreditsChanged();
    } catch (err) {
      report(err);
    } finally {
      setBusy(false);
    }
  };

  const buyPaypal = (productId: string) =>
    run('PayPal 订阅', async () => {
      const created = await api<{ subscription_id: string; id: string; status: string; product_id: string; plan_id: string }>('/api/billing/paypal/subscriptions', { method: 'POST', body: { product_id: productId } });
      const { data } = await api<PurchaseResult>(`/api/billing/paypal/subscriptions/${encodeURIComponent(created.data.subscription_id)}/confirm`, { method: 'POST' });
      return { refresh: data.token_refresh_required, text: data.granted ? `PayPal · ${data.subscription?.status} 至 ${fmt(data.subscription?.current_period_end ?? null)}` : '该订阅已处理过（幂等）', payload: { created: created.data, confirm: data } };
    });

  const buySubscription = (productId: string, days: number) =>
    run('模拟订阅购买', async () => {
      const tx = mockId('sub');
      const receipt = JSON.stringify({ transaction_id: tx, original_transaction_id: tx, expires_at: daysFromNow(days), auto_renew: true });
      const { data } = await api<PurchaseResult>('/api/billing/purchases/verify', { method: 'POST', body: { platform, kind: 'subscription', product_id: productId, receipt } });
      setOriginalTx(tx);
      return { refresh: data.token_refresh_required, text: data.granted ? `已发放 · ${data.subscription?.status} 至 ${fmt(data.subscription?.current_period_end ?? null)}` : '该交易已处理过（幂等）', payload: data };
    });

  const restore = () =>
    run('恢复购买', async () => {
      const receipt = JSON.stringify({ transactions: [{ kind: 'subscription', transaction_id: originalTx || mockId('restore'), original_transaction_id: originalTx || undefined, product_id: catalog?.subscriptions[0]?.id ?? 'pro_monthly', expires_at: daysFromNow(30) }] });
      const { data } = await api<RestoreResult>('/api/billing/restore', { method: 'POST', body: { platform, receipt } });
      return { refresh: data.token_refresh_required, text: `订阅 ${data.subscription.status} · 补发钻石 ${data.credits_synced}`, payload: data, credits: data.credits_synced > 0 };
    });

  const webhook = (type: string, extra: Record<string, unknown> = {}) =>
    run(`模拟商店通知 ${type}`, async () => {
      const body = { type, transaction_id: mockId(type.toLowerCase()), original_transaction_id: originalTx || undefined, product_id: catalog?.current.product_id ?? 'pro_monthly', ...extra };
      const { data } = await api<{ received: true; applied: string[] }>(`/api/billing/webhooks/${platform}`, { method: 'POST', body, auth: false });
      const applied = data.applied.join(', ');
      // the webhook path cannot tell us whether *our* tier changed: refresh whenever the notification touched the subscription
      return { refresh: /expired|revoked|extended|in_grace|granted/.test(applied), text: applied, payload: data };
    });

  if (!me) return null;
  const current = catalog?.current;
  return (
    <Section title="Pro 订阅 · GET /api/me/subscription · POST /api/billing/purchases/verify" actions={<button className="btn-ghost" onClick={() => void load()}>刷新</button>}>
      <Message text={error} kind="error" />
      <Message text={message} kind="ok" />
      {catalog && current && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Pill tone={STATUS_TONE[current.status]}>{STATUS_LABEL[current.status]}</Pill>
            <Pill tone={me.vip_tier === 'pro' ? 'indigo' : 'zinc'}>令牌档位 {me.vip_tier}</Pill>
            {current.status !== 'none' && (
              <span className="text-xs text-zinc-400">
                {current.product_id} · {current.period === 'year' ? '年付' : '月付'} · {current.platform} · 到期 {fmt(current.current_period_end)} · 自动续订 {current.auto_renew ? '开' : '关'} · {current.environment}
                {current.management_url && <> · <a className="underline" href={current.management_url} target="_blank" rel="noreferrer">商店管理</a></>}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-zinc-400">模拟平台</span>
            {(['apple', 'google'] as const).map((p) => (
              <button key={p} className={p === platform ? 'btn' : 'btn-ghost'} onClick={() => setPlatform(p)}>{p}</button>
            ))}
            <Pill tone={catalog.mock_iap ? 'amber' : 'zinc'}>{catalog.mock_iap ? 'mock_iap 开：接受 mock 收据' : 'mock_iap 关：需要真实商店收据'}</Pill>
            <Pill tone={catalog.paypal_client_id ? 'indigo' : 'zinc'}>{catalog.paypal_client_id ? `PayPal client_id ${catalog.paypal_client_id.slice(0, 12)}…` : 'PayPal 未配置'}</Pill>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {catalog.subscriptions.map((s) => (
              <div key={s.id} className="rounded-lg border border-zinc-800 p-2 text-xs">
                <div className="flex items-center justify-between"><span className="font-medium">{s.period === 'year' ? 'Pro 年付' : 'Pro 月付'} · ${s.usd}</span>{s.period === 'year' && <Pill tone="amber">省 {Math.round((1 - s.usd / ((catalog.subscriptions.find((m) => m.period === 'month')?.usd ?? s.usd) * 12)) * 100)}%</Pill>}</div>
                <div className="text-zinc-500">{s.id} · ios {s.store_product_ids.ios ?? '—'} · android {s.store_product_ids.android ?? '—'} · paypal {s.store_product_ids.paypal ?? '—'}</div>
                <button className="btn mt-2" disabled={busy || !catalog.mock_iap} onClick={() => void buySubscription(s.id, s.period === 'year' ? 365 : 30)}>模拟购买</button>
                {(catalog.mock_iap || (catalog.paypal_client_id && s.store_product_ids.paypal)) && (
                  <button className="btn-ghost mt-2 ml-1" disabled={busy} onClick={() => void buyPaypal(s.id)}>PayPal 订</button>
                )}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <button className="btn-ghost" disabled={busy || !catalog.mock_iap} onClick={() => void restore()}>恢复购买（restore）</button>
            {isAdmin && current.status !== 'none' && (
              <>
                <button className="btn-ghost" disabled={busy || !catalog.mock_iap} onClick={() => void webhook('DID_RENEW', { expires_at: daysFromNow(60) })}>通知 DID_RENEW（+60d）</button>
                <button className="btn-ghost" disabled={busy || !catalog.mock_iap} onClick={() => void webhook('DID_CHANGE_RENEWAL_STATUS', { auto_renew: !(current.auto_renew ?? true) })}>通知 关/开 自动续订</button>
                <button className="btn-ghost" disabled={busy || !catalog.mock_iap} onClick={() => void webhook('GRACE_PERIOD')}>通知 宽限期</button>
                <button className="btn-ghost" disabled={busy || !catalog.mock_iap} onClick={() => void webhook('EXPIRED')}>通知 EXPIRED（降级）</button>
                <button className="btn-ghost" disabled={busy || !catalog.mock_iap} onClick={() => void webhook('REVOKE')}>通知 REVOKE（退款撤销）</button>
              </>
            )}
          </div>
          <p className="text-xs text-zinc-500">
            购买成功后服务端已把 vip_tier 改成 pro；旧 access token 的 tier claim 过期，gateway 对下一请求返回 401 AUTH_TOKEN_EXPIRED (reason=tier_changed)。console 收到 token_refresh_required 时立即刷新令牌，所以这里看不到那次 401。
          </p>
          {last !== null && <Json value={last} className="max-h-56" />}
        </>
      )}
    </Section>
  );
}

/** Mock consumable purchase for the credits page (`kind = credits`). */
export async function mockBuyPack(platform: 'apple' | 'google', packId: string): Promise<PurchaseResult> {
  const receipt = JSON.stringify({ transaction_id: mockId(`pack-${packId}`) });
  return (await api<PurchaseResult>('/api/billing/purchases/verify', { method: 'POST', body: { platform, kind: 'credits', product_id: packId, receipt } })).data;
}
